import CIcon from '@coreui/icons-react';
import { CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle } from '@coreui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { NotificationType } from '@transferz/fe-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { authorisedClient } from '../../apollo/authorisedClient';
import { useAuth } from '../../auth/useAuth';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useUserMeQuery } from '../../hooks/useUserQuery';
import { routeConfig } from '../../navigation/routeConfig';
import { ToasterContext } from '../shared/Toaster/ToasterContext';

interface IUserProfileProps {
  setDropdownOpen: (isDropdownOpen: boolean) => void;
}

export const UserProfile: React.FunctionComponent<IUserProfileProps> = ({ setDropdownOpen }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { loading, data } = useUserMeQuery();
  const { removeToken } = useAuth();
  const flags = useFlags();
  const { addToast } = useContext(ToasterContext);
  const [token] = useLocalStorage('authToken');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dropdownRef.current) {
      const observer = new MutationObserver(() => setDropdownOpen(!!dropdownRef.current?.classList.contains('show')));
      observer.observe(dropdownRef.current, {
        attributes: true,
        characterData: false,
        childList: false,
        subtree: false,
      });
      return () => observer.disconnect();
    }
  }, [dropdownRef.current]);

  function copyToken () {
    if (token?.accessToken) {
      navigator.clipboard.writeText(token?.accessToken);
      addToast({
        title: 'Token copied',
        children: 'Token was copied to clipboard',
        type: NotificationType.Success,
      });
    }
  }

  useEffect(() => {
    if (data?.me) {
      let { me: { name, email, id } } = data;
      datadogRum.setUser({
        name, email, id,
      });
      datadogLogs.setUser({
        name, email, id,
      });
    }
  }, [data]);

  if (loading || !data) {
    return null;
  }

  const { me: { name } } = data;

  const handleClick = () => {
    removeToken();
    authorisedClient.clearStore();
    history.push('/logged-out');
  };

  return (
    <CDropdown inNav className="c-header-nav-items mx-2" innerRef={dropdownRef}>
      <CDropdownToggle className="c-header-nav-link" caret={true} >
        {flags.fancyGreeting ? <i>Salutations! </i> : t('header.hiThere')} {name}
      </CDropdownToggle>
      <CDropdownMenu className="pt-0" placement="bottom-end" >
        <CDropdownItem to={routeConfig.profile.path}>
          <CIcon name="cil-user" className="mfe-2" />Profile
        </CDropdownItem>
        <CDropdownItem divider />
        <CDropdownItem>
          <CIcon name="cil-copy" className="journey-info-copy copy-icon"/>
          <span className="ml-2" onClick={copyToken}>Copy token</span>
        </CDropdownItem>
        <CDropdownItem divider />
        <CDropdownItem onClick={handleClick}>
          <CIcon name="cil-account-logout" className="mfe-2" />Log out
        </CDropdownItem>
      </CDropdownMenu>
    </CDropdown>
  );
};
